import { useSearchParams } from 'react-router';
import { useBotRoute } from '../lib/useBotRoute';
import { QUALITY_TABS, qualityTab, type QualityTab } from '../lib/testerView';
import { AnswersReviewPanel } from '../components/quality/AnswersReviewPanel';
import { TestsPanel } from '../components/quality/TestsPanel';
import { RunView } from '../components/quality/RunView';
import { SuggestionsPanel } from '../components/quality/SuggestionsPanel';
import { LatinTextPanel } from '../components/quality/LatinTextPanel';

const TAB_LABEL: Record<QualityTab, string> = {
  review: 'Answers to review',
  tests: 'Tests',
  suggestions: 'Suggestions',
  latin: 'Latin text',
};

/**
 * Bot workspace → Quality: Answers to review (answer-quality Phase 2a), Tests and Suggestions (tester spec chatbot_v2_mono
 * 2026-10-12-tester-suggestions-design.md §4.4). The tab is in the URL (`?tab=tests|suggestions`; v1's chatbot_front
 * links there), and a run's page is `?tab=tests&run=<jobId>`. Latin text (`?tab=latin`): what customers typed in Latin
 * and what it became (Mungun/infra text-conversion review, P1).
 */
export function BotQuality() {
  const { system, botId } = useBotRoute();
  const [params, setParams] = useSearchParams();
  const tab = qualityTab(params.get('tab'));
  const run = params.get('run');
  const key = `${system}/${botId}`;
  const go = (t: QualityTab) => setParams(t === 'review' ? {} : { tab: t });
  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold">Quality</h1>
        <p className="text-sm text-gray-600">How the bot’s AI answers did, tests of its knowledge, and the document fixes they suggest.</p>
      </div>
      <div className="flex gap-1 border-b border-gray-200" role="tablist">
        {QUALITY_TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => go(t)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === t ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-gray-600 hover:text-gray-900'}`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>
      {/* Keyed by bot: nothing loaded for — or still in flight from — the previous bot reaches this one. */}
      {tab === 'review' && <AnswersReviewPanel key={key} system={system} botId={botId} />}
      {tab === 'tests' &&
        (run ? (
          <RunView key={`${key}/${run}`} system={system} botId={botId} jobId={run} onBack={() => setParams({ tab: 'tests' })} />
        ) : (
          <TestsPanel key={key} system={system} botId={botId} />
        ))}
      {tab === 'suggestions' && <SuggestionsPanel key={key} system={system} botId={botId} />}
      {tab === 'latin' && <LatinTextPanel key={key} system={system} botId={botId} />}
    </div>
  );
}
